import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    Fade,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialog-paper": {
        borderRadius: "12px",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        width: "100%",
        maxWidth: "500px",
        margin: theme.spacing(1.5),
        overflow: "hidden",
        animation: "fadeIn 0.3s ease-in-out",
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(1),
            maxWidth: "90%",
        },
    },
    "@keyframes fadeIn": {
        from: { opacity: 0, transform: "translateY(20px)" },
        to: { opacity: 1, transform: "translateY(0)" },
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    background: "linear-gradient(90deg, #6366f1, #a855f7)",
    color: "#ffffff",
    padding: theme.spacing(1.5, 2.5),
    display: "flex",
    alignItems: "center",
    marginBottom: "5px",
    justifyContent: "space-between",
    fontSize: "1.25rem",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
        fontSize: "1rem",
        padding: theme.spacing(1, 1.5),
    },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: "transparent",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1.5),
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    margin: theme.spacing(0.5, 0),
    "& .MuiInputBase-root": {
        borderRadius: "6px",
        backgroundColor: "#ffffff",
        border: "1px solid #e0e7ff",
        paddingTop: "8px",
        height: "40px",
        transition: "all 0.2s ease",
        "&:hover": {
            borderColor: "#6366f1",
        },
    },
    "& .MuiInputBase-input": {
        padding: "8px 12px",
    },
    "& .MuiInputLabel-root": {
        color: "#6b7280",
        fontWeight: 500,
        transform: "translate(12px, 8px) scale(1)",
        "&.Mui-focused, &.MuiFormLabel-filled": {
            transform: "translate(12px, -6px) scale(0.7)",
            color: "#6366f1",
            background: "#ffffff",
            padding: "0 3px",
        },
    },
    "& .MuiInputBase-root.Mui-focused": {
        borderColor: "#6366f1",
        boxShadow: "0 0 0 2px rgba(99, 102, 241, 0.15)",
    },
    "& .MuiInputBase-multiline": {
        height: "auto",
        padding: "8px 12px",
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: "6px",
    padding: theme.spacing(1, 2.5),
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.9rem",
    margin: theme.spacing(0.5),
    transition: "all 0.2s ease",
    "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
    [theme.breakpoints.down("sm")]: {
        width: "100%",
        margin: theme.spacing(0.3, 0),
    },
}));

// Simple email validation regex
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const SendVoucherLink = ({ open, onClose, onSend, initialEmail, residentName, loading }) => {
    const theme = useTheme();
    const [emailData, setEmailData] = useState({
        email: initialEmail || "",
        alternateEmail: "",
        subject: "Payment Voucher Document",
        body: `Dear ${residentName || "Resident"},\n\nPlease find attached the payment voucher details.\n\nBest regards,\nClanbridge Living Pvt Ltd`,
    });
    const [errors, setErrors] = useState({
        email: false,
        alternateEmail: false,
    });

    useEffect(() => {
        if (open) {
            setEmailData((prev) => ({
                ...prev,
                email: initialEmail || "",
                body: `Dear ${residentName || "Resident"},\n\nPlease find attached the payment voucher details.\n\nBest regards,\nClanbridge Living Pvt Ltd`,
            }));
            setErrors({ email: false, alternateEmail: false });
        }
    }, [initialEmail, residentName, open]);

    // Handle input changes for email form fields
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEmailData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Validate email fields on change
        if (name === "email") {
            setErrors((prev) => ({
                ...prev,
                [name]: value.trim() && !isValidEmail(value) ? "Invalid email format" : false,
            }));
        } else if (name === "alternateEmail") {
            // Split alternateEmail by commas and validate each email
            const emails = value.split(",").map((email) => email.trim()).filter(Boolean);
            const invalidEmails = emails.filter((email) => !isValidEmail(email));
            setErrors((prev) => ({
                ...prev,
                alternateEmail: invalidEmails.length > 0 ? "Email addresses are invalid" : false,
            }));
        }

    };

    // Trigger the onSend callback with email data when Send button is clicked
    const handleSend = () => {
        const { email, alternateEmail, subject, body } = emailData;

        // Validate registered email
        const isRegisteredEmailValid = email.trim() ? isValidEmail(email) : true;

        // Split alternateEmail by commas and validate each email
        const alternateEmails = alternateEmail
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean); // Remove empty strings
        const isAlternateEmailValid = alternateEmails.every((email) => isValidEmail(email));

        // Update error states
        setErrors({
            email: email.trim() && !isRegisteredEmailValid ? "Invalid registered email format" : false,
            alternateEmail: alternateEmails.length > 0 && !isAlternateEmailValid ? "One or more alternate email addresses are invalid" : false,
        });

        // Check if at least one valid email is provided
        if (!email.trim() && !alternateEmail.trim()) {
            toast.error("Please provide at least one valid email address.");
            return;
        }

        if (!isRegisteredEmailValid || (alternateEmails.length > 0 && !isAlternateEmailValid)) {
            toast.error("Please correct the invalid email format(s).");
            return;
        }

        // Prioritize alternate email if provided and valid, otherwise use registered email
        // const selectedEmail = alternateEmail.trim() && isAlternateEmailValid ? alternateEmail : email;
        // if (!selectedEmail) {
        //     toast.error("No valid email provided to send the payment voucher.");
        //     return;
        // }


        const selectedEmail = alternateEmail.trim() && isAlternateEmailValid ? alternateEmail : null;

        const toEmails = [];
        if (email.trim() && isRegisteredEmailValid) {
            toEmails.push(email);
        }
        if (alternateEmails.length > 0 && isAlternateEmailValid) {
            toEmails.push(...alternateEmails);
        }

        if (toEmails.length === 0) {
            toast.error("No valid email provided to send the payment advice.");
            return;
        }
        // Call onSend with the selected email
        onSend({ subject, body, toEmails });
    };

    return (
        <StyledDialog open={open} onClose={onClose} TransitionComponent={Fade} transitionDuration={300} aria-labelledby="email-modal-title">
            <StyledDialogTitle id="email-modal-title">Send Payment Voucher</StyledDialogTitle>
            <StyledDialogContent>
                <Grid container spacing={0.3}>
                    <Grid item xs={12}>
                        <StyledTextField
                            fullWidth
                            label="Registered Email"
                            name="email"
                            value={emailData.email}
                            onChange={handleInputChange}
                            variant="outlined"
                            disabled={!!initialEmail}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <StyledTextField
                            fullWidth
                            label="Alternate Email"
                            name="alternateEmail"
                            value={emailData.alternateEmail}
                            onChange={handleInputChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.alternateEmail}
                            helperText={errors.alternateEmail || "You can enter multiple emails separated by commas"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <StyledTextField
                            fullWidth
                            label="Subject"
                            name="subject"
                            value={emailData.subject}
                            onChange={handleInputChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <StyledTextField
                            fullWidth
                            label="Message"
                            name="body"
                            value={emailData.body}
                            onChange={handleInputChange}
                            variant="outlined"
                            multiline
                            rows={4}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </StyledDialogContent>
            <DialogActions
                sx={{
                    padding: 2,
                    justifyContent: "end",
                    gap: 0.5,
                    flexWrap: "wrap",
                    [theme.breakpoints.down("sm")]: {
                        padding: 1.5,
                        flexDirection: "column",
                    },
                }}
            >
                <StyledButton
                    variant="contained"
                    sx={{ backgroundColor: "#6366f1", "&:hover": { backgroundColor: "#a855f7" } }}
                    startIcon={<SendIcon />}
                    onClick={handleSend}
                    disabled={loading}
                >
                    {loading ? "Sending..." : "Send"}
                </StyledButton>
                <StyledButton
                    variant="outlined"
                    sx={{
                        borderColor: "#6366f1",
                        color: "#6366f1",
                        "&:hover": { borderColor: "#a855f7", color: "#a855f7" },
                    }}
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </StyledButton>
            </DialogActions>
        </StyledDialog>
    );
};

export default SendVoucherLink;